import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert, Card, Row, Col, Spinner } from 'react-bootstrap'
import {
  useAdminIssue,
  useCreateAdminIssue,
  useUpdateAdminIssue,
  useDeleteAdminIssue
} from 'api/hooks/use-admin-issues'

const DATATYPE_OPTIONS = [
  'Employee', 'EmployeePermission', 'EmployeeConflict',
  'Role', 'RolePermission', 'RoleConflict',
  'BusinessRule', 'Conflict', 'Permission', 'Account'
]

const IssueForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: issue, isLoading } = useAdminIssue({ id })
  const createMutation = useCreateAdminIssue({ options: { onSuccess: () => navigate('/admin/issues') } })
  const updateMutation = useUpdateAdminIssue({ id, options: { onSuccess: () => navigate('/admin/issues') } })
  const deleteMutation = useDeleteAdminIssue({ options: { onSuccess: () => navigate('/admin/issues') } })

  const [formData, setFormData] = useState({
    number: '',
    name: '',
    description: '',
    resolution: '',
    default_severity: 2,
    datatype: ''
  })

  useEffect(() => {
    if (issue) {
      setFormData({
        number: issue.number || '',
        name: issue.name || '',
        description: issue.description || '',
        resolution: issue.resolution || '',
        default_severity: issue.default_severity || 2,
        datatype: issue.datatype || ''
      })
    }
  }, [issue])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const submitData = { ...formData }
    if (submitData.datatype === '') submitData.datatype = null
    if (isEditing) {
      updateMutation.mutate(submitData)
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this issue?')) {
      deleteMutation.mutate(id)
    }
  }

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Issue' : 'Create Issue'}</h2>

      {error && <Alert variant="danger" className="mt-3">{error.message || 'An error occurred'}</Alert>}

      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Number</Form.Label>
                  <Form.Control type="number" name="number" value={formData.number} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Resolution</Form.Label>
              <Form.Control as="textarea" rows={3} name="resolution" value={formData.resolution} onChange={handleChange} />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Default Severity</Form.Label>
                  <Form.Select name="default_severity" value={formData.default_severity} onChange={handleChange}>
                    <option value={1}>1 - Low</option>
                    <option value={2}>2 - Medium</option>
                    <option value={3}>3 - High</option>
                    <option value={4}>4 - Critical</option>
                    <option value={5}>5 - Severe</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Datatype</Form.Label>
                  <Form.Select name="datatype" value={formData.datatype} onChange={handleChange}>
                    <option value="">None</option>
                    {DATATYPE_OPTIONS.map((dt) => (
                      <option key={dt} value={dt}>{dt}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button type="submit" variant="primary" disabled={isSubmitting} className="me-2">
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="light-primary" onClick={() => navigate('/admin/issues')} disabled={isSubmitting}>
                  Cancel
                </Button>
              </div>
              {isEditing && (
                <Button type="button" variant="danger" onClick={handleDelete} disabled={isSubmitting}>
                  Delete Issue
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default IssueForm
